import { createSlice } from '@reduxjs/toolkit';

import { SliceSelectors } from 'state/meter-type/meter-type-slice';

import type { TMeter } from 'api/models/TMeter';
import type { NormalizedError } from 'errors/types';

export type MeterPointValidationState = {
  validationRequired: {
    electricity: boolean | null;
    gas: boolean | null;
  };
  userMeterpoints: {
    MPRN: string | null;
    MPAN: string | null;
  };
  data: TMeter[];
  isLoading: boolean;
  errors: NormalizedError[] | null;
};

export const initialState: MeterPointValidationState = {
  validationRequired: {
    electricity: null,
    gas: null
  },
  userMeterpoints: {
    MPRN: null,
    MPAN: null
  },
  data: [],
  isLoading: false,
  errors: null
};

export const slice = createSlice({
  name: 'meterpointValidation',
  initialState,
  reducers: {
    setInitialState: (state) => {
      Object.entries(initialState).forEach(
        ([key, value]) => (state[key] = value)
      );
    },
    setMPRN: (state, { payload }) => {
      state.userMeterpoints.MPRN = payload;
    },
    setMPAN: (state, { payload }) => {
      state.userMeterpoints.MPAN = payload;
    },
    setValidationRequired: (state, { payload }) => {
      state.validationRequired = payload;
    },
    resetData: (state) => {
      state.data = initialState.data;
    },
    resetErrors: (state) => {
      state.errors = initialState.errors;
    },
    setIsLoading: (state) => {
      state.isLoading = true;
      state.errors = initialState.errors;
    },
    setSuccess: (state, { payload }) => {
      state.isLoading = false;
      state.data = payload.meterpoints;
      state.errors = payload.errors;
    },
    setError: (state, { payload }) => {
      state.isLoading = false;
      state.errors = payload;
    }
  }
});

export const {
  setInitialState,
  setMPRN,
  setMPAN,
  setValidationRequired,
  setIsLoading,
  setSuccess,
  setError,
  resetData,
  resetErrors
} = slice.actions;

const defaultSelectors = new SliceSelectors(slice.name);

const {
  selectSlice,
  selectGasMeter,
  selectElectricityMeter
} = defaultSelectors;

export const selectUserMeterpoints = (state) =>
  selectSlice(state).userMeterpoints;

export const selectValidationRequired = (state) =>
  selectSlice(state).validationRequired;

export const selectMeterAddresses = (state) => {
  const { electricity, gas } = selectValidationRequired(state);
  const electricityMeter = selectElectricityMeter(state);
  const gasMeter = selectGasMeter(state);

  return {
    ...(electricity && {
      electricity: electricityMeter ? electricityMeter.address : null
    }),
    ...(gas && { gas: gasMeter ? gasMeter.address : null })
  };
};
export const selectValidationErrors = (state) => {
  const errors = defaultSelectors.selectErrors(state) || [];

  return errors.filter(
    (error) => error && error.keyword?.toLowerCase() === 'validation'
  );
};

export const selectors = {
  ...defaultSelectors,
  selectUserMeterpoints,
  selectValidationRequired,
  selectMeterAddresses,
  selectValidationErrors
};

export default slice.reducer;
